import FieldLabel from '@components/Common/FieldLabel';
import FormField from '@components/Common/FormField';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import React from 'react';

import { isAllowedHref, linkTags, wrapSelection } from './richText';

interface RichTextFieldProps {
  name: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  error?: string;
  warning?: string;
}

/**
 * A prose field with the three inline marks the site keeps: bold, italic and a link.
 *
 * Deliberately a textarea with a toolbar, not a WYSIWYG widget. What is stored is the markup the
 * generator wrote, and the site renders it through a sanitiser that keeps exactly these three
 * tags; a rich editor would emit its own markup (spans, divs, inline styles) that the sanitiser
 * then strips, and the author would watch formatting vanish between the editor and the page.
 */
const RichTextField: React.FC<RichTextFieldProps> = ({ name, label, value, onChange, error, warning }) => {
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [linking, setLinking] = React.useState<{ start: number; end: number } | null>(null);
  const [href, setHref] = React.useState('');
  const [hrefError, setHrefError] = React.useState('');

  const selection = () => {
    const el = inputRef.current;
    return { start: el?.selectionStart ?? value.length, end: el?.selectionEnd ?? value.length };
  };

  const apply = (before: string, after: string, range = selection()) => {
    const next = wrapSelection(value, range.start, range.end, before, after);
    onChange(next.value);
    // Put the caret back around what was wrapped, once React has written the new value.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(next.selectionStart, next.selectionEnd);
    });
  };

  const insertLink = () => {
    if (!linking) return;
    if (!isAllowedHref(href)) {
      setHrefError('Use a site path such as /hiring-guides/slug, or a full https:// address.');
      return;
    }
    const { before, after } = linkTags(href);
    apply(before, after, linking);
    setLinking(null);
    setHref('');
    setHrefError('');
  };

  return (
    <Box className="ge-rich">
      <Box className="ge-rich__head">
      <FieldLabel htmlFor={name}>{label}</FieldLabel>
      <Box className="ge-rich__tools" role="toolbar" aria-label={`Formatting for ${label}`}>
        <Tooltip title="Bold">
          <IconButton size="small" aria-label="Bold" onClick={() => apply('<strong>', '</strong>')}>
            <FormatBoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic">
          <IconButton size="small" aria-label="Italic" onClick={() => apply('<em>', '</em>')}>
            <FormatItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Link">
          <IconButton
            size="small"
            aria-label="Link"
            aria-expanded={!!linking}
            onClick={() => {
              setLinking(selection());
              setHrefError('');
            }}
          >
            <InsertLinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      </Box>
      {linking && (
        <Box className="ge-rich__link">
          <FormField
            required
            name={`${name}-href`}
            label="Link address"
            value={href}
            error={!!hrefError}
            helperText={hrefError || 'The selected words become the link.'}
            placeholder="/hiring-guides/how-to-hire-a-bench-jeweller"
            onChange={(_n, v) => {
              setHref(String(v ?? ''));
              if (hrefError) setHrefError('');
            }}
            textFieldProps={{
              autoFocus: true,
              onKeyDown: (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  insertLink();
                }
              },
            }}
          />
          <Box className="ge-rich__link-actions">
            <Button size="small" variant="contained" onClick={insertLink}>
              Insert link
            </Button>
            <Button size="small" onClick={() => setLinking(null)}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}
      <FormField
        required={false}
        multiline
        name={name}
        label=""
        value={value}
        error={!!error}
        helperText={error || warning || 'Bold, italic and links only. Leave a blank line between paragraphs.'}
        textFieldProps={{ rows: undefined, minRows: 3, inputRef }}
        onChange={(_n, v) => onChange(String(v ?? ''))}
      />
    </Box>
  );
};

export default RichTextField;
